import { Platform } from 'react-native';

export type ThemeId = 'win95' | 'win2000' | 'platinum';

export interface ThemeColors {
  /** 3D face — dialogs, buttons, panel backgrounds. */
  face: string;
  /** Outer top/left of a raised edge. */
  highlight: string;
  /** Inner top/left of a raised edge. */
  light: string;
  /** Inner bottom/right of a raised edge. */
  shadow: string;
  /** Outer bottom/right of a raised edge. */
  darkShadow: string;

  windowText: string;
  disabledText: string;
  disabledHighlight: string;
  fieldBg: string;

  titleActiveLeft: string;
  titleActiveRight: string;
  titleInactiveLeft: string;
  titleInactiveRight: string;
  titleText: string;
  titleTextInactive: string;

  selectionBg: string;
  selectionText: string;

  /** Behind the windows. */
  desktop: string;

  /** The running man, and other accents. */
  accent: string;
  linkBlue: string;
  idleText: string;
  awayText: string;
}

export interface Theme {
  id: ThemeId;
  /** Shown in the settings picker. */
  label: string;
  /** One line of context for the picker. */
  description: string;
  colors: ThemeColors;
  fonts: { ui: string; message: string };
  fontSizes: { ui: number; title: number; message: number };
  metrics: {
    titleBarHeight: number;
    titleButton: number;
    windowPadding: number;
    touchTarget: number;
    /** Platinum rounded its buttons slightly; the Windows themes never did. */
    radius: number;
  };
  /**
   * How the caption bar is drawn. `gradient` is the Windows left-to-right
   * fade; `pinstripe` is Mac OS Platinum's horizontal rules with a centred
   * title and the close box on the left.
   */
  titleBar: {
    style: 'gradient' | 'pinstripe';
    align: 'left' | 'center';
    buttons: 'left' | 'right';
  };
}

const touchTarget = Platform.OS === 'ios' || Platform.OS === 'android' ? 44 : 22;

/**
 * Windows 95. The reference the whole app was built against: the exact system
 * palette, MS Sans Serif at 11px, and a teal desktop.
 */
const win95: Theme = {
  id: 'win95',
  label: 'Windows 95',
  description: 'The original. Teal desktop, MS Sans Serif.',
  colors: {
    face: '#C0C0C0',
    highlight: '#FFFFFF',
    light: '#DFDFDF',
    shadow: '#808080',
    darkShadow: '#000000',
    windowText: '#000000',
    disabledText: '#808080',
    disabledHighlight: '#FFFFFF',
    fieldBg: '#FFFFFF',
    titleActiveLeft: '#000080',
    titleActiveRight: '#1084D0',
    titleInactiveLeft: '#808080',
    titleInactiveRight: '#B5B5B5',
    titleText: '#FFFFFF',
    titleTextInactive: '#C0C0C0',
    selectionBg: '#000080',
    selectionText: '#FFFFFF',
    desktop: '#008080',
    accent: '#FFCC00',
    linkBlue: '#0000EE',
    idleText: '#808080',
    awayText: '#666666',
  },
  fonts: {
    ui: 'W95FA',
    message: Platform.select({ default: 'Times New Roman' }) as string,
  },
  fontSizes: { ui: 11, title: 11, message: 13 },
  metrics: { titleBarHeight: 18, titleButton: 16, windowPadding: 3, touchTarget, radius: 0 },
  titleBar: { style: 'gradient', align: 'left', buttons: 'right' },
};

/**
 * Windows 2000. Warmer grey than 95, a deeper blue caption, Tahoma instead of
 * MS Sans Serif, and the darker outer shadow that made 2000's bevels tighter.
 */
const win2000: Theme = {
  id: 'win2000',
  label: 'Windows 2000',
  description: 'Warmer grey, deep blue caption, Tahoma.',
  colors: {
    face: '#D4D0C8',
    highlight: '#FFFFFF',
    light: '#E8E5E0',
    shadow: '#808080',
    // 2000 used a lighter outer shadow than 95's pure black.
    darkShadow: '#404040',
    windowText: '#000000',
    disabledText: '#808080',
    disabledHighlight: '#FFFFFF',
    fieldBg: '#FFFFFF',
    titleActiveLeft: '#0A246A',
    titleActiveRight: '#A6CAF0',
    titleInactiveLeft: '#808080',
    titleInactiveRight: '#C0C0C0',
    titleText: '#FFFFFF',
    titleTextInactive: '#D8D8D8',
    selectionBg: '#0A246A',
    selectionText: '#FFFFFF',
    desktop: '#3A6EA5',
    accent: '#FFCC00',
    linkBlue: '#0000EE',
    idleText: '#808080',
    awayText: '#666666',
  },
  fonts: {
    ui: Platform.select({ windows: 'Tahoma', default: 'Tahoma' }) as string,
    message: Platform.select({ default: 'Times New Roman' }) as string,
  },
  fontSizes: { ui: 11, title: 11, message: 13 },
  metrics: { titleBarHeight: 20, titleButton: 16, windowPadding: 3, touchTarget, radius: 0 },
  titleBar: { style: 'gradient', align: 'left', buttons: 'right' },
};

/**
 * Mac OS 8/9 Platinum. Light grey throughout, pinstriped caption bars with the
 * title centred and the close box on the left, and gently rounded controls.
 */
const platinum: Theme = {
  id: 'platinum',
  label: 'Mac OS 9',
  description: 'Platinum grey, pinstriped title bars, Charcoal.',
  colors: {
    face: '#DDDDDD',
    highlight: '#FFFFFF',
    light: '#EEEEEE',
    shadow: '#999999',
    darkShadow: '#666666',
    windowText: '#000000',
    disabledText: '#999999',
    disabledHighlight: '#FFFFFF',
    fieldBg: '#FFFFFF',
    // Platinum captions are grey with darker pinstripes, not a colour fade.
    titleActiveLeft: '#CCCCCC',
    titleActiveRight: '#BBBBBB',
    titleInactiveLeft: '#E4E4E4',
    titleInactiveRight: '#DDDDDD',
    titleText: '#000000',
    titleTextInactive: '#888888',
    selectionBg: '#3366CC',
    selectionText: '#FFFFFF',
    desktop: '#5C7A99',
    accent: '#FF9500',
    linkBlue: '#0000EE',
    idleText: '#888888',
    awayText: '#666666',
  },
  fonts: {
    ui: Platform.select({ default: 'Charcoal' }) as string,
    message: Platform.select({ default: 'Geneva' }) as string,
  },
  fontSizes: { ui: 12, title: 12, message: 13 },
  metrics: { titleBarHeight: 20, titleButton: 14, windowPadding: 4, touchTarget, radius: 3 },
  titleBar: { style: 'pinstripe', align: 'center', buttons: 'left' },
};

export const THEMES: Record<ThemeId, Theme> = { win95, win2000, platinum };

export const THEME_LIST: Theme[] = [win95, win2000, platinum];

export const DEFAULT_THEME_ID: ThemeId = 'win95';

export function getTheme(id: string | null | undefined): Theme {
  return THEMES[(id as ThemeId) ?? DEFAULT_THEME_ID] ?? THEMES[DEFAULT_THEME_ID];
}
